import { useState, useEffect } from 'react';
import {
  X,
  User,
  Mail,
  Phone,
  Globe,
  MapPin,
  Building,
  CreditCard,
  FileText,
  Globe as LanguageIcon,
  Coins,
  Wallet,
  Check,
  Plane,
  Hotel,
  Car,
  Activity as ActivityIcon,
  BookOpen,
  ChevronDown,
  Settings,
  Package
} from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import api from '../../api/axios';
import { toast } from 'react-toastify';

const CustomerModal = ({ supplier, type, onClose, onSave, lang }) => {
  const { t } = useTranslation(lang);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city_id: '',
    country_id: '',
    postal_code: '',
    notes: '',
    language: 'en',
    currency: 'EUR',
    commission_hotel: 0,
    commission_flight: 0,
    commission_transfer: 0,
    commission_activity: 0,
    deposit: 0,
    balance: 0,
    pay_with_credit_card: 1,
    pay_with_deposit: 0,
    confirm_refundable_booking: 1,
    has_activity: 0,
    has_hotel: 1,
    has_transfer: 1,
    has_flight: 1,
    has_booking: 1,
  });

  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [showFeaturesDropdown, setShowFeaturesDropdown] = useState(false);
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);

  useEffect(() => {
    console.log('dfzefezf', supplier);
    
    if (supplier && type === 'edit') {
      setFormData({
        name: supplier.name || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || '',
        city_id: supplier.city?.id || '',
        country_id: supplier.country?.id || '',
        postal_code: supplier.postal_code || '',
        notes: supplier.notes || '',
        language: supplier.language || 'en',
        currency: supplier.currency || 'EUR',
        commission_hotel: supplier.commission_hotel || 0,
        commission_flight: supplier.commission_flight || 0,
        commission_transfer: supplier.commission_transfer || 0,
        commission_activity: supplier.commission_activity || 0,
        deposit: supplier.deposit || 0,
        balance: supplier.balance || 0,
      pay_with_credit_card: supplier.pay_with_credit_card ? 1 : 0,
      pay_with_deposit: supplier.pay_with_deposit ? 1 : 0,
      confirm_refundable_booking: supplier.confirm_refundable_booking ? 1 : 0,
      has_activity: supplier.has_activity ? 1 : 0,
      has_hotel: supplier.has_hotel ? 1 : 0,
      has_transfer: supplier.has_transfer ? 1 : 0,
      has_flight: supplier.has_flight ? 1 : 0,
      has_booking: supplier.has_booking ? 1 : 0
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        city_id: '',
        country_id: '',
        postal_code: '',
        notes: '',
        language: 'en',
        currency: 'EUR',
        commission_hotel: 0,
        commission_flight: 0,
        commission_transfer: 0,
        commission_activity: 0,
        deposit: 0,
        balance: 0,
        pay_with_credit_card: 0,
        pay_with_deposit: 0,
        confirm_refundable_booking: 0,
        has_activity: 0,
        has_hotel: 0,
        has_transfer: 0,
        has_flight: 0,
        has_booking: 0,
      });
    }
  }, [supplier, type]);

  // Fetch countries on component mount
  useEffect(() => {
    fetchAllCountries();
  }, [lang]);

  // Fetch cities when country changes
  useEffect(() => {
    if (formData.country_id) {
      fetchCitiesByCountry(formData.country_id);
    } else {
      setCities([]);
    }
  }, [formData.country_id, lang]);

  const fetchAllCountries = async () => {
    setCountriesLoading(true);
    try {
      const firstResponse = await api.get(process.env.REACT_APP_API_COUNTRY_URL);
      const firstData = firstResponse.data;
      const lastPage = firstData.last_page;

      let allCountries = [...firstData.data];

      if (lastPage > 1) {
        const promises = [];

        for (let page = 2; page <= lastPage; page++) {
          promises.push(
            api.get(process.env.REACT_APP_API_COUNTRY_URL, {
              params: { page }
            }).then(res => res.data.data)
          );
        }

        const pagesResults = await Promise.all(promises);
        pagesResults.forEach(pageData => {
          allCountries.push(...pageData);
        });
      }

      const formattedCountries = allCountries.map(country => ({
        id: country.id,
        name: country[lang] || country.en || country.fr || country.es || country.id,
        code: country.id
      })).sort((a, b) => a.name.localeCompare(b.name));

      setCountries(formattedCountries);

    } catch (error) {
      console.error('Erreur lors du chargement des pays:', error);
      toast.error(t('errorLoadingCountries'));
    } finally {
      setCountriesLoading(false);
    }
  };

  const fetchCitiesByCountry = async (countryId) => {
    setCitiesLoading(true);
    try {
      const firstResponse = await api.get(`${process.env.REACT_APP_API_CITY_URL}?country_id=${countryId}`);
      const firstData = firstResponse.data;
      const lastPage = firstData.last_page;

      let allCities = [...firstData.data];

      if (lastPage > 1) {
        const promises = [];

        for (let page = 2; page <= lastPage; page++) {
          promises.push(
            api.get(`${process.env.REACT_APP_API_CITY_URL}?country_id=${countryId}`, {
              params: { page }
            }).then(res => res.data.data)
          );
        }

        const pagesResults = await Promise.all(promises);
        pagesResults.forEach(pageData => {
          allCities.push(...pageData);
        });
      }

      const formattedCities = allCities.map(city => ({
        id: city.id,
        name: city[lang] || city.en || city.fr || city.es || city.id,
        country_id: city.country_id
      })).sort((a, b) => a.name.localeCompare(b.name));

      setCities(formattedCities);

    } catch (error) {
      console.error('Erreur lors du chargement des villes:', error);
      toast.error(t('errorLoadingCities'));
    } finally {
      setCitiesLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePhoneChange = (value) => {
    // Permettre uniquement les chiffres, espaces, +, (), -
    const cleanedValue = value.replace(/[^\d\s\+\-\(\)]/g, '');
    handleChange('phone', cleanedValue);
  };

  const handleNumberChange = (field, value) => {
    // Permettre uniquement les chiffres et point décimal
    const cleanedValue = value.replace(/[^\d.]/g, '');
    handleChange(field, cleanedValue);
  };

  const handleCommissionChange = (field, value) => {
    // Nettoyer la valeur
    let cleanedValue = value.replace(/[^\d.]/g, '');

    // Limiter à 4 chiffres maximum
    const parts = cleanedValue.split('.');
    if (parts[0].length > 4) {
      parts[0] = parts[0].slice(0, 4);
    }
    if (parts[1] && parts[1].length > 2) {
      parts[1] = parts[1].slice(0, 2);
    }

    cleanedValue = parts.join('.');

    // Convertir en nombre et limiter à 100%
    const numValue = parseFloat(cleanedValue);
    if (!isNaN(numValue) && numValue > 100) {
      cleanedValue = '100';
    }

    handleChange(field, cleanedValue);
  };

  const toggleFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      [feature]: prev[feature] ? 0 : 1
    }));
  };

  const togglePaymentOption = (option) => {
    setFormData(prev => ({
      ...prev,
      [option]: prev[option] ? 0 : 1
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.toString().trim()) {
      newErrors.name = t('nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('invalidEmail');
    }

    if (formData.phone.trim() && !/[\d]/.test(formData.phone)) {
      newErrors.phone = t('invalidPhone');
    }

    if (formData.address.length < 5) {
      newErrors.address = t('addressTooShort');
    }

    // Validation des commissions
    const commissionFields = [
      'commission_hotel',
      'commission_flight',
      'commission_transfer',
      'commission_activity'
    ];

    commissionFields.forEach(field => {
      const value = parseFloat(formData[field]);
      if (!isNaN(value) && value > 100) {
        newErrors[field] = t('commissionMaxError');
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const errorMessages = Object.values(errors).join(', ');
      toast.error(`Veuillez corriger: ${errorMessages}`);
      return;
    }

    const customerData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      city_id: formData.city_id,
      country_id: formData.country_id,
      postal_code: formData.postal_code,
      status: formData.status,
      notes: formData.notes,
      language: formData.language,
      currency: formData.currency,
      commission_hotel: formData.commission_hotel || 0,
      commission_flight: formData.commission_flight || 0,
      commission_transfer: formData.commission_transfer || 0,
      commission_activity: formData.commission_activity || 0,
      deposit: formData.deposit || 0,
      balance: formData.balance || 0,
      pay_with_credit_card: formData.pay_with_credit_card ? 1 : 0,
      pay_with_deposit: formData.pay_with_deposit ? 1 : 0,
      confirm_refundable_booking: formData.confirm_refundable_booking ? 1 : 0,
      has_activity: formData.has_activity ? 1 : 0,
      has_hotel: formData.has_hotel ? 1 : 0,
      has_transfer: formData.has_transfer ? 1 : 0,
      has_flight: formData.has_flight ? 1 : 0,
      has_booking: formData.has_booking ? 1 : 0,
    };

    try {
      const response = await api.post(`/customers/${supplier.id}/update`, customerData);

      // Succès
      onSave(customerData);
      toast.success(t('customerUpdatedSuccessfully'));

    } catch (error) {
      let errorMessage = 'Une erreur est survenue';

      if (error.response?.data?.request?.message) {
        errorMessage = error.response.data.request.message;
      }
      else if (error.response?.data?.result?.Error?.message) {
        errorMessage = error.response.data.result.Error.message;
      }
      else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      else if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        errorMessage = Object.values(errors)
          .flat()
          .join(', ');
      }
      else if (!error.response) {
        errorMessage = 'Erreur de connexion. Vérifiez votre réseau.';
      }

      // Afficher avec toast
      toast.error(errorMessage);
    }
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modal: {
      background: 'white',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '900px',
      minWidth: '300px',
      maxHeight: '90vh',
      overflow: 'hidden',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '24px',
      borderBottom: '1px solid #e5e7eb',
      flexShrink: 0,
      backgroundColor: 'white'
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px',
      borderRadius: '4px',
      color: '#6b7280'
    },
    content: {
      padding: '24px',
      overflow: 'auto',
      flex: 1
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    },
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      position: 'relative'
    },
    fullWidthField: {
      gridColumn: 'span 2'
    },
    dropdownButton: {
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      backgroundColor: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%'
    },
    dropdownContent: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      zIndex: 10,
      marginTop: '4px',
      padding: '12px',
      maxHeight: '300px',
      overflowY: 'auto'
    },
    checkboxItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    checkboxItemHover: {
      backgroundColor: '#f3f4f6'
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151'
    },
    input: {
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      transition: 'all 0.2s'
    },
    numberInput: {
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      textAlign: 'right'
    },
    textarea: {
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      minHeight: '80px',
      resize: 'vertical'
    },
    inputError: {
      borderColor: '#ef4444'
    },
    inputDisabled: {
      backgroundColor: '#f9fafb',
      color: '#6b7280',
      cursor: 'not-allowed'
    },
    select: {
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      backgroundColor: 'white',
      width: '100%'
    },
    error: {
      color: '#ef4444',
      fontSize: '12px',
      marginTop: '4px'
    },
    helpText: {
      color: '#6b7280',
      fontSize: '12px',
      marginTop: '4px',
      fontStyle: 'italic'
    },
    footer: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      padding: '24px',
      borderTop: '1px solid #e5e7eb'
    },
    button: {
      padding: '10px 20px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    buttonPrimary: {
      backgroundColor: '#7c3aed',
      color: 'white'
    },
    buttonSecondary: {
      backgroundColor: '#6b7280',
      color: 'white'
    },
    sectionTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '12px',
      paddingBottom: '8px',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    commissionGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)', // 4 colonnes égales
      gap: '15px',
      marginBottom: '10px'
    },
    commissionFieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      position: 'relative'
    },
    commissionInput: {
      padding: '12px 30px 12px 12px', // Plus d'espace pour le %
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      width: '100%',
      textAlign: 'right'
    },
    commissionLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      fontWeight: '500',
      color: '#374151',
      whiteSpace: 'nowrap'
    },
    commissionIcon: {
      flexShrink: 0
    },
    commissionPercentage: {
      position: 'absolute',
      marginTop: '15px',
      right: '12px',
      top: '38px',
      color: '#6b7280',
      fontSize: '14px',
      fontWeight: '500'
    }
  };

  const currencies = [
    { code: 'EUR', name: 'Euro' },
    { code: 'USD', name: 'US Dollar' },
    { code: 'GBP', name: 'British Pound' },
    { code: 'MAD', name: 'Moroccan Dirham' },
    { code: 'CAD', name: 'Canadian Dollar' },
    { code: 'AUD', name: 'Australian Dollar' },
    { code: 'JPY', name: 'Japanese Yen' },
    { code: 'CNY', name: 'Chinese Yuan' },
    { code: 'CHF', name: 'Swiss Franc' },
    { code: 'AED', name: 'UAE Dirham' },
    { code: 'SAR', name: 'Saudi Riyal' },
    { code: 'QAR', name: 'Qatari Riyal' },
    { code: 'KWD', name: 'Kuwaiti Dinar' },
    { code: 'BHD', name: 'Bahraini Dinar' },
    { code: 'OMR', name: 'Omani Rial' },
    { code: 'TRY', name: 'Turkish Lira' },
    { code: 'RUB', name: 'Russian Ruble' },
    { code: 'INR', name: 'Indian Rupee' },
    { code: 'BRL', name: 'Brazilian Real' },
    { code: 'ZAR', name: 'South African Rand' }
  ];

  const features = [
    { key: 'has_hotel', label: t('hasHotel'), icon: <Hotel size={14} /> },
    { key: 'has_flight', label: t('hasFlight'), icon: <Plane size={14} /> },
    { key: 'has_transfer', label: t('hasTransfer'), icon: <Car size={14} /> },
    { key: 'has_activity', label: t('hasActivity'), icon: <ActivityIcon size={14} /> },
    { key: 'has_booking', label: t('hasBooking'), icon: <BookOpen size={14} /> }
  ];

  const paymentOptions = [
    { key: 'pay_with_credit_card', label: t('payWithCreditCard'), icon: <CreditCard size={14} /> },
    { key: 'pay_with_deposit', label: t('payWithDeposit'), icon: <Wallet size={14} /> },
    { key: 'confirm_refundable_booking', label: t('confirmRefundableBooking'), icon: <Check size={14} /> }
  ];

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.title}>
            <Building size={20} />
            {type === 'add' && t('addCustomer')}
            {type === 'edit' && t('editCustomer')}
          </div>
          <button
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          <form onSubmit={handleSubmit} style={styles.form}>

            {/* Informations de base */}
            <div style={styles.sectionTitle}>{t('basicInformation')}</div>
            <div style={styles.grid}>
              {/* Nom */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <User size={16} />
                  {t('name')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  style={{
                    ...styles.input,
                    ...(errors.name && styles.inputError)
                  }}
                  placeholder={t('enterName')}
                />
                {errors.name && <div style={styles.error}>{errors.name}</div>}
              </div>

              {/* Email */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <Mail size={16} />
                  {t('email')}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  style={{
                    ...styles.input,
                    ...(errors.email && styles.inputError)
                  }}
                  placeholder={t('enterEmail')}
                />
                {errors.email && <div style={styles.error}>{errors.email}</div>}
              </div>

              {/* Téléphone */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <Phone size={16} />
                  {t('phone')}
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  maxLength={12}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  style={{
                    ...styles.input,
                    ...(errors.phone && styles.inputError)
                  }}
                  placeholder={t('enterPhone')}
                />
                {errors.phone && <div style={styles.error}>{errors.phone}</div>}
                <div style={styles.helpText}>{t('phoneExample')}</div>
              </div>
            </div>

            {/* Adresse */}
            <div style={styles.sectionTitle}>{t('addressInformation')}</div>
            <div style={styles.grid}>
              {/* Adresse */}
              <div style={{ ...styles.fieldGroup, ...styles.fullWidthField }}>
                <label style={styles.label}>
                  <MapPin size={16} />
                  {t('address')}
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  style={{
                    ...styles.input,
                    ...(errors.address && styles.inputError)
                  }}
                  placeholder={t('enterAddress')}
                />
                {errors.address && <div style={styles.error}>{errors.address}</div>}
              </div>

              {/* Pays */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <Globe size={16} />
                  {t('country')}
                </label>
                <select
                  value={formData.country_id}
                  onChange={(e) => handleChange('country_id', e.target.value)}
                  style={styles.select}
                  disabled={countriesLoading}
                >
                  <option value="">{t('selectCountry')}</option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
                {countriesLoading && <div style={styles.helpText}>{t('loading')}...</div>}
              </div>

              {/* Ville */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <Building size={16} />
                  {t('city')}
                </label>
                <select
                  value={formData.city_id}
                  onChange={(e) => handleChange('city_id', e.target.value)}
                  style={styles.select}
                  disabled={citiesLoading || !formData.country_id}
                >
                  <option value="">{t('selectCity')}</option>
                  {cities.map(city => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
                {citiesLoading && <div style={styles.helpText}>{t('loading')}...</div>}
                {!formData.country_id && <div style={styles.helpText}>{t('selectCountryFirst')}</div>}
              </div>

              {/* Code postal */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <MapPin size={16} />
                  {t('postalCode')}
                </label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => handleChange('postal_code', e.target.value)}
                  style={styles.input}
                  placeholder={t('enterPostalCode')}
                />
              </div>
            </div>

            {/* Commission et paiement */}
            <div style={styles.sectionTitle}>{t('commissionAndPayment')}</div>
            <div style={styles.commissionGrid}>
              {/* Commission Hôtel */}
              <div style={styles.commissionFieldGroup}>
                <label style={styles.commissionLabel}>
                  <span style={styles.commissionIcon}><Hotel size={14} /></span>
                  {t('commissionHotel')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    value={formData.commission_hotel}
                    onChange={(e) => handleCommissionChange('commission_hotel', e.target.value)}
                    style={{
                      ...styles.commissionInput,
                      ...(errors.commission_hotel && styles.inputError)
                    }}
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                  <div style={styles.commissionPercentage}>%</div>
                </div>
                {errors.commission_hotel && <div style={styles.error}>{errors.commission_hotel}</div>}
              </div>

              {/* Commission Vol */}
              <div style={styles.commissionFieldGroup}>
                <label style={styles.commissionLabel}>
                  <span style={styles.commissionIcon}><Plane size={14} /></span>
                  {t('commissionFlight')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    value={formData.commission_flight}
                    onChange={(e) => handleCommissionChange('commission_flight', e.target.value)}
                    style={{
                      ...styles.commissionInput,
                      ...(errors.commission_flight && styles.inputError)
                    }}
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                  <div style={styles.commissionPercentage}>%</div>
                </div>
                {errors.commission_flight && <div style={styles.error}>{errors.commission_flight}</div>}
              </div>

              {/* Commission Transfert */}
              <div style={styles.commissionFieldGroup}>
                <label style={styles.commissionLabel}>
                  <span style={styles.commissionIcon}><Car size={14} /></span>
                  {t('commissionTransfer')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    value={formData.commission_transfer}
                    onChange={(e) => handleCommissionChange('commission_transfer', e.target.value)}
                    style={{
                      ...styles.commissionInput,
                      ...(errors.commission_transfer && styles.inputError)
                    }}
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                  <div style={styles.commissionPercentage}>%</div>
                </div>
                {errors.commission_transfer && <div style={styles.error}>{errors.commission_transfer}</div>}
              </div>

              {/* Commission Activité */}
              <div style={styles.commissionFieldGroup}>
                <label style={styles.commissionLabel}>
                  <span style={styles.commissionIcon}><ActivityIcon size={14} /></span>
                  {t('commissionActivity')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    value={formData.commission_activity}
                    onChange={(e) => handleCommissionChange('commission_activity', e.target.value)}
                    style={{
                      ...styles.commissionInput,
                      ...(errors.commission_activity && styles.inputError)
                    }}
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                  <div style={styles.commissionPercentage}>%</div>
                </div>
                {errors.commission_activity && <div style={styles.error}>{errors.commission_activity}</div>}
              </div>
            </div>

            {/* Dépôt et Solde (reste en grille 2 colonnes) */}
            <div style={styles.grid}>
              {/* Dépôt */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <Wallet size={16} />
                  {t('deposit')}
                </label>
                <input
                  type="number"
                  value={formData.deposit}
                  onChange={(e) => handleNumberChange('deposit', e.target.value)}
                  style={styles.numberInput}
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>

              {/* Solde */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <Coins size={16} />
                  {t('balance')}
                </label>
                <input
                  type="number"
                  value={formData.balance}
                  onChange={(e) => handleNumberChange('balance', e.target.value)}
                  style={styles.numberInput}
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            {/* Paramètres */}
            <div style={styles.sectionTitle}>
              <Settings size={16} />
              {t('settings')}
            </div>
            <div style={styles.grid}>
              {/* Langue */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <LanguageIcon size={16} />
                  {t('language')}
                </label>
                <select
                  value={formData.language}
                  onChange={(e) => handleChange('language', e.target.value)}
                  style={styles.select}
                >
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                  <option value="es">Español</option>
                  <option value="ar">العربية</option>
                  <option value="de">Deutsch</option>
                  <option value="it">Italiano</option>
                  <option value="pt">Português</option>
                </select>
              </div>

              {/* Devise */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <Coins size={16} />
                  {t('currency')}
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => handleChange('currency', e.target.value)}
                  style={styles.select}
                >
                  {currencies.map(curr => (
                    <option key={curr.code} value={curr.code}>
                      {curr.code} ({curr.name})
                    </option>
                  ))}
                </select>
              </div>

              {/* Dropdown pour les fonctionnalités */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <Package size={16} />
                  {t('features')}
                </label>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    style={styles.dropdownButton}
                    onClick={() => setShowFeaturesDropdown(!showFeaturesDropdown)}
                  >
                    <span>{t('selectFeatures')}</span>
                    <ChevronDown size={16} />
                  </button>

                  {showFeaturesDropdown && (
                    <div style={styles.dropdownContent}>
                      {features.map(feature => (
                        <div
                          key={feature.key}
                          style={{
                            ...styles.checkboxItem,
                            ...(formData[feature.key] && { backgroundColor: '#e0e7ff' })
                          }}
                          onClick={() => toggleFeature(feature.key)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = formData[feature.key] ? '#e0e7ff' : 'transparent'}
                        >
                          <input
                            type="checkbox"
                            checked={formData[feature.key]}
                            onChange={() => { }}
                            style={{ pointerEvents: 'none' }}
                          />
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {feature.icon}
                            <span>{feature.label}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Dropdown pour les options de paiement */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <CreditCard size={16} />
                  {t('paymentOptions')}
                </label>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    style={styles.dropdownButton}
                    onClick={() => setShowPaymentDropdown(!showPaymentDropdown)}
                  >
                    <span>{t('selectPaymentOptions')}</span>
                    <ChevronDown size={16} />
                  </button>

                  {showPaymentDropdown && (
                    <div style={styles.dropdownContent}>
                      {paymentOptions.map(option => (
                        <div
                          key={option.key}
                          style={{
                            ...styles.checkboxItem,
                            ...(formData[option.key] && { backgroundColor: '#e0e7ff' })
                          }}
                          onClick={() => togglePaymentOption(option.key)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = formData[option.key] ? '#e0e7ff' : 'transparent'}
                        >
                          <input
                            type="checkbox"
                            checked={formData[option.key]}
                            onChange={() => { }}
                            style={{ pointerEvents: 'none' }}
                          />
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {option.icon}
                            <span>{option.label}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div style={{ ...styles.fieldGroup, ...styles.fullWidthField }}>
                <label style={styles.label}>
                  <FileText size={16} />
                  {t('notes')}
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  style={styles.textarea}
                  placeholder={t('enterNotes')}
                />
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button
            type="button"
            style={{ ...styles.button, ...styles.buttonSecondary }}
            onClick={onClose}
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            style={{ ...styles.button, ...styles.buttonPrimary }}
            onClick={handleSubmit}
          >
            {t('save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerModal;